import AmendmentOriginalButton from "./AmendmentOriginalButton";
/**
 * 검색결과 종합 뷰 — CLHS 5-섹션 패리티. URL: ?audit=onto-detail&sq=<검색어>
 *
 *  ① 관세율표 — TariffCompareTable. 데이터는 LeftAiPanel doSearch 응답을
 *     props(tariffData) 재사용, 새로고침/딥링크로 없으면 자체 keyword fetch 폴백.
 *  ② 분류사례(관세청 clip_kcs·bigdata) ┐ precedents-search keyword 1회 fetch 공유,
 *  ⑤ 결정례·사전심사(pre_ruling·clip_foreign) ┘ splitPrecedentsBySource 로 분리.
 *     ⚠keyword 모드 사용: hybrid 는 hs4 앵커 없이 호출하면 전체 시맨틱 스캔으로
 *     PG statement_timeout(30s) 초과→500 (종합 뷰는 검색 시점 단일 hs4 없음).
 *     keyword 는 0.13s·호 횡단(clip_kcs·bigdata·clip_foreign 모두 반환).
 *  ③ 주·해설 — notes-search keyword  ④ 관련법령 — laws-search keyword
 *
 * 섹션별 독립 병렬 fetch — 느린 섹션이 다른 섹션을 막지 않음.
 * 0건 섹션도 헤더 유지(흐리게) — "다각도 확인" 신뢰 신호 (CLHS 동일).
 * 섹션 fetch 실패 시 해당 섹션에만 "불러오기 실패 · 재시도".
 */
import React, { useCallback, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import TariffCompareTable from "./TariffCompareTable";
import { useSearchData } from "./useSearchData";
import SearchRefinementPanel from "./SearchRefinementPanel";
import ProductIdentityGate from "./ProductIdentityGate";
import { decideProductGrounding } from "./multiSearchData";
import SubdivisionPreviewCard from "./SubdivisionPreviewCard";
import ProposalCard, { HistoryMatchLine, isProposalReady } from "./ProposalCard";
import ExemptionHintLine, { useExemptionCodeQuery } from "./ExemptionHintLine";
import { copyTreeFacts } from "./treeFactStore";
import NaverMeaningCandidates, { buildNaverExpandedQuery } from "./NaverMeaningCandidates";
import { useAuditSearch } from "./AuditSearchContext";
import SourceEvidenceLink, {
  precedentEvidence,
} from "./SourceEvidenceLink";
import {
  buildHighlightSnippet,
  buildSearchHighlightTokens,
  highlightTokens,
  textHasHighlightToken,
} from "./highlightTokens";

function SectionCard({ title, count, loading, error, onRetry, children }) {
  const [open, setOpen] = useState(true);
  const empty = !loading && !error && count === 0;
  return (
    <div
      style={{
        border: "1px solid var(--audit-border)",
        borderRadius: 6,
        marginBottom: 12,
        background: "var(--audit-bg)",
        opacity: empty ? 0.65 : 1,
      }}
    >
      <div
        onClick={() => !empty && setOpen((v) => !v)}
        style={{
          padding: "8px 12px",
          background: "var(--audit-bg-elevated)",
          borderBottom: open && !empty ? "1px solid var(--audit-border)" : "none",
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: empty ? "default" : "pointer",
          fontSize: 12,
        }}
      >
        {!empty && (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
        <span style={{ fontWeight: 600 }}>{title}</span>
        <span
          style={{
            marginLeft: "auto",
            fontFamily: "JetBrains Mono, monospace",
            fontSize: 12,
            color: "var(--audit-text-muted)",
          }}
        >
          {loading ? "..." : error ? "" : `${count}건`}
        </span>
        {error && onRetry && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRetry();
            }}
            style={{
              border: "1px solid var(--audit-border)",
              background: "transparent",
              color: "#d93025",
              fontSize: 12,
              borderRadius: 4,
              cursor: "pointer",
              padding: "1px 8px",
              fontFamily: "inherit",
            }}
          >
            불러오기 실패 · 재시도
          </button>
        )}
      </div>
      {open && loading && (
        <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--audit-text-muted)" }}>
          불러오는 중…
        </div>
      )}
      {open && !loading && !error && count > 0 && (
        <div style={{ padding: "8px 12px" }}>{children}</div>
      )}
    </div>
  );
}

// ②·⑤ 공용 사례 테이블 — HSK · 품명 · 시행일 · 원문 근거
// superseded(변경고시로 폐기된 결정)는 숨기지 않고 보조줄로 표시 — 연혁도 상품.
export function CaseTable({ items, onOpen, showSourceBadge, matchTokens = [] }) {
  const SOURCE_BADGE = { pre_ruling: "사전심사", clip_foreign: "외국" };
  return (
    <table className="lhs-case-table" style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th style={caseTh}>HSK</th>
          <th style={{ ...caseTh, width: "auto" }}>품명 / 사례</th>
          <th style={{ ...caseTh, width: 90 }}>시행일</th>
          <th style={{ ...caseTh, width: 52, textAlign: "center" }}>근거</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => {
          const superseded = it.status === "superseded" && it.amendment;
          const label = String(it.label || "");
          const generatedSnippet = [it.desc, it.reasoning]
            .map((text) => buildHighlightSnippet(text, matchTokens))
            .find(Boolean);
          const matchSnippet = String(it.match_snippet || generatedSnippet || "");
          const bodyOnlyMatch = !textHasHighlightToken(label, matchTokens);
          return (
          <React.Fragment key={it.case_id}>
          <tr
            onClick={() => onOpen(it.case_id, it)}
            style={{ borderTop: "1px solid var(--audit-border)", cursor: "pointer" }}
            title="클릭 — 상세 보기"
          >
            <td style={{ padding: 4, fontFamily: "JetBrains Mono, monospace", fontWeight: 600 }}>
              {it.hsk || it.hs6 || ""}
            </td>
            <td style={{ padding: 4 }}>
              {showSourceBadge && SOURCE_BADGE[it.source] && (
                <span
                  style={{
                    fontSize: 12,
                    border: "1px solid var(--audit-border)",
                    borderRadius: 3,
                    padding: "0 4px",
                    marginRight: 6,
                    color: "var(--audit-text-muted)",
                  }}
                >
                  {SOURCE_BADGE[it.source]}
                </span>
              )}
              {superseded && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    border: "1px solid #f0c36d",
                    background: "#fef7e0",
                    color: "#b06000",
                    borderRadius: 3,
                    padding: "0 4px",
                    marginRight: 6,
                  }}
                >
                  변경
                </span>
              )}
              {highlightTokens(label, matchTokens)}
              {matchSnippet && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 6,
                    marginTop: 3,
                    color: "var(--audit-text-muted)",
                    fontSize: 11.5,
                    lineHeight: 1.45,
                  }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      border: "1px solid var(--audit-border)",
                      borderRadius: 3,
                      background: "#fefce8",
                      color: "#a16207",
                      padding: "0 4px",
                      fontWeight: 700,
                    }}
                  >
                    {bodyOnlyMatch ? "본문 일치" : "본문"}
                  </span>
                  <span>{highlightTokens(matchSnippet, matchTokens)}</span>
                </div>
              )}
            </td>
            <td style={{ padding: 4, fontFamily: "JetBrains Mono, monospace", color: "var(--audit-text-muted)" }}>
              {it.decision_date ? String(it.decision_date).slice(0, 10) : ""}
            </td>
            <td style={{ padding: 4, textAlign: "center" }}>
              <SourceEvidenceLink
                compact
                iconOnly
                {...precedentEvidence({
                  decisionUrl: it.decision_url,
                  caseId: it.case_id,
                  source: it.source,
                  code: it.hsk || it.hs6,
                  refNo: it.ref_no,
                })}
              />
            </td>
          </tr>
          {superseded && (
            <tr
              onClick={() => onOpen(it.case_id, it)}
              style={{ cursor: "pointer" }}
              title="변경고시로 품목분류가 변경된 결정 — 클릭 시 상세"
            >
              <td colSpan={4} style={{ padding: "2px 4px 6px 16px", fontSize: 11.5, color: "#b06000" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span>
                    └ 변경됨 →{" "}
                    <b style={{ fontFamily: "JetBrains Mono, monospace" }}>
                      {it.amendment.integrity_status === "matched" && it.amendment.hs_after
                        ? it.amendment.hs_after
                        : it.amendment.integrity_status === "revoked"
                          ? "결정 폐지(대체 세번 없음)"
                          : "변경세번 연결 검증 필요 — 자동 표시 중지"}
                    </b>
                    {" "}(제{it.amendment.gosi_no}호{it.amendment.gosi_date ? `, ${it.amendment.gosi_date}` : ""})
                  </span>
                  <AmendmentOriginalButton amendment={it.amendment} hsk={it.hsk} />
                </span>
              </td>
            </tr>
          )}
          </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
const caseTh = {
  textAlign: "left",
  padding: 4,
  width: 110,
  color: "var(--audit-text-muted)",
};

// 10자리 숫자 세번 → 3926.40-0000 표기(이미 포맷된 값은 그대로).
function fmtHsk(v) {
  const d = String(v || "").replace(/[^0-9]/g, "");
  if (d.length === 10) return `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6)}`;
  return String(v || "");
}

// 품명 정확 일치 정규화 — 소문자·기호/공백 제거. 사례 title은 "150cm( Plastic" 류 공백 변형이 흔해
// 서버 exact 티어(공백제거 결합어의 PGroonga 매칭)가 영문 다단어에서 못 잡는 것을 보강한다.
function normName(s) {
  return String(s || "").toLowerCase().replace(/[^0-9a-z가-힣]/g, "");
}

/**
 * 즉답 카드 — 검색어가 국내 분류사례 품명과 정확 일치(tier=exact 또는 정규화 동일)하면 즉답.
 * 변경고시로 폐기된 결정이면 정답 자리는 변경 후 세번, 종전 결정·고시번호·변경사유는 근거로.
 * 일치 사례들의 답이 갈리면 렌더하지 않는다(즉답 강요 금지 — 부당확정 방지).
 */
function ExactAnswerCard({ q, cases, onOpen }) {
  const nq = normName(q);
  // 품명 일치: 정규화 동일 또는 접두 포함(커버리지 ≥80%) — 사례 title 꼬리의 원산지("; KR;") 등
  // 부가 표기 차이를 흡수. 짧은 이름의 우연 접두 일치는 길이 하한(10)으로 차단.
  const nameHit = (label) => {
    const nl = normName(label);
    if (nq.length >= 6 && nl === nq) return true;
    if (nq.length < 10 || nl.length < 10) return false;
    return (nl.startsWith(nq) || nq.startsWith(nl))
      && Math.min(nl.length, nq.length) / Math.max(nl.length, nq.length) >= 0.8;
  };
  const exact = (cases || []).filter(
    (it) => (it.hsk || it.hs6)
      && (it.tier === "exact" || nameHit(it.label))
      // 정확한 before→after 쌍이 검증되지 않은 변경결정은 즉답에서 완전히 제외한다.
      && !(it.status === "superseded"
        && !(it.amendment
          && it.amendment.integrity_status === "matched"
          && it.amendment.hs_after)),
  );
  if (!exact.length) return null;
  const answerOf = (it) =>
    it.status === "superseded"
      && it.amendment
      && it.amendment.integrity_status === "matched"
      && it.amendment.hs_after
      ? it.amendment.hs_after
      : it.hsk || it.hs6;
  const answers = [...new Set(exact.map((it) => fmtHsk(answerOf(it))))];
  if (answers.length !== 1) return null;
  const top = exact.find((it) => it.status === "superseded" && it.amendment) || exact[0];
  const superseded = top.status === "superseded" && top.amendment;
  const reason = superseded && top.amendment.reason
    ? String(top.amendment.reason).replace(/^변경사유\s*[-–]?\s*/, "").split(/\(적용일\)/)[0].trim()
    : null;
  return (
    <div
      style={{
        border: superseded ? "1px solid #f0c36d" : "1px solid var(--audit-border)",
        borderLeft: superseded ? "3px solid #b06000" : "3px solid #137333",
        borderRadius: 6,
        background: "var(--audit-bg)",
        padding: "10px 14px",
        marginBottom: 12,
      }}
    >
      <div style={{ fontSize: 12, color: "var(--audit-text-muted)", marginBottom: 4 }}>
        이 품명의 분류사례가 있습니다{superseded ? " — 품목분류가 변경된 물품입니다" : ""}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 20, fontWeight: 700 }}>
          {answers[0]}
        </span>
        {superseded && (
          <span style={{ fontSize: 12, color: "#b06000" }}>
            종전 <s style={{ fontFamily: "JetBrains Mono, monospace" }}>{fmtHsk(top.hsk || top.hs6)}</s>
            {" "}→ 제{top.amendment.gosi_no}호
            {top.amendment.gosi_date ? ` (${top.amendment.gosi_date})` : ""}
          </span>
        )}
      </div>
      {reason && (
        <div style={{ fontSize: 12, color: "var(--audit-text)", marginTop: 6, lineHeight: 1.6 }}>
          <b style={{ color: "#b06000" }}>변경사유</b> {reason}
        </div>
      )}
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 7 }}
      >
        <button
          type="button"
          onClick={() => onOpen && onOpen(top.case_id, top)}
          style={{ border: 0, padding: 0, background: "transparent", color: "var(--audit-text-muted)", fontSize: 11.5, cursor: "pointer", fontFamily: "inherit" }}
          title="근거 사례 상세 보기"
        >
          근거: {top.label} · {top.decision_date ? String(top.decision_date).slice(0, 10) : ""} — 상세 보기
        </button>
        <SourceEvidenceLink
          compact
          {...precedentEvidence({
            decisionUrl: top.decision_url,
            caseId: top.case_id,
            source: top.source,
            code: top.hsk || top.hs6,
            refNo: top.ref_no,
          })}
        />
        {superseded && (
          <AmendmentOriginalButton amendment={top.amendment} hsk={top.hsk} />
        )}
      </div>
    </div>
  );
}

/**
 * 제안 카드의 "참고 강조" 를 이 화면의 행 클릭 계약으로 옮긴다.
 *
 * 카드는 세번 하나만 안다. 이 화면의 다른 소비자(`TariffCompareTable`·`SearchRefinementPanel`)
 * 와 부모(`HeadingDetailPage`)는 `(hs4, hsk)` 두 인자를 쓴다. 전에는 객체 하나를 넘겨
 * `?hs=[object Object]` 로 주소가 바뀌고 `hsk` 가 지워진 채 **검색 결과를 떠났다** — 카드
 * 주석이 약속한 "참고 강조만" 과 정반대였다(코드 리뷰 v9). 카드 시험은 `onHighlight` 를
 * 모의로 바꿔 끼워 이 연결을 보지 못했다.
 *
 * 함수로 빼 둔 이유는 시험이 **같은 것을** 부를 수 있게 하기 위해서다.
 */
export function proposalHighlight(onRowClick) {
  return (hsk) => {
    if (!onRowClick) return;
    const code = String(hsk || "").replace(/\D/g, "");
    if (code.length < 4) return;                 // 모양이 아니면 아무 데도 가지 않는다
    onRowClick(code.slice(0, 4), code.length === 10 ? code : null);
  };
}

export default function MultiSearchResults({
  q,
  tariffData,
  subdivisionPreview,
  onRowClick,
  onPrecedentOpen,
  onOpenProve,
  data,
}) {
  // 제안 카드 응답(우리 사무소·LLM 후보)을 사전 점검 카드의 지도·표 마디에도 붙인다. 요청은 제안 카드가 한 번만 한다.
  const [proposalData, setProposalData] = useState(null);
  // "A088000101" 같은 감면부호 검색은 물품 검색이 아니다 — 아래 물품용 카드들을 돌리지 않는다.
  const exemptionCodeQuery = useExemptionCodeQuery(q);
  // 인라인 화살표를 카드에 주면 응답마다 카드가 다시 묻는 되풀이가 됐다(ProposalCard 주석 참조).
  const onProposalLoaded = useCallback((data) => {
    setProposalData(data ? { query: q, data } : null);
    // 관세사 신고이력 탭(다른 패널)에 LLM이 읽은 영문·법령어 품명을 알린다 — 이력 품명은 영문이라 한글 검색어가 0건일 때 다리(51차).
    const llm = data && data.signals && data.signals.llm;
    if (llm && (llm.name_en || llm.name_ko)) {
      try { window.dispatchEvent(new CustomEvent("loginhs:proposal-loaded", { detail: { query: q, name_en: llm.name_en || "", name_en_generic: llm.name_en_generic || "", name_ko: llm.name_ko || "" } })); } catch (_) { /* ignore */ }
    }
  }, [q]);
  const { setPendingSearch, lookupInFlight } = useAuditSearch();
  // data 주입 시(탭 셸이 useSearchData 를 공유) 자체 fetch 생략(중복 방지);
  // 없으면 스스로 fetch. hook 은 항상 호출(조건부 금지) — data 있으면 q=null 로 무동작.
  const auto = useSearchData(data ? null : q, tariffData);
  const d = data || auto;

  if (!q) return null;

  const tariff = d.tariff;
  // 제안 카드는 관세율표 조회가 끝난 뒤 한 번만 묻는다(상품 설명은 그 응답에 실려 온다).
  const proposalReady = isProposalReady({ q, tariffData, lookupInFlight, tariff });
  const tariffCount = tariff
    ? (tariff.items || []).length + (tariff.headingMatches || []).length
    : 0;
  const cases = d.cases;
  const rulings = d.rulings;
  const prec = d.precState;
  const notes = d.notesState;
  const laws = d.lawsState;
  const retryPrec = d.retry.prec;
  const retryNotes = d.retry.notes;
  const retryLaws = d.retry.laws;
  const tariffMatchTokens = buildSearchHighlightTokens(q, tariff?.matchTokens);
  const precedentMatchTokens = buildSearchHighlightTokens(q, prec.matchTokens);
  const noteMatchTokens = buildSearchHighlightTokens(q, notes.matchTokens);
  const lawMatchTokens = buildSearchHighlightTokens(q, laws.matchTokens);
  const productGroundingMode = decideProductGrounding({
    q,
    tariffCount,
    cases,
    notes: notes.items,
    laws: laws.items,
    localLoading: prec.loading || notes.loading || laws.loading,
  });

  return (
    <div className="audit-fade-in" style={{ marginTop: 8 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 8,
          display: "flex",
          alignItems: "baseline",
          gap: 8,
        }}
      >
        검색결과 종합
        <span style={{ fontSize: 12, color: "var(--audit-text-muted)", fontWeight: 400 }}>
          "{q}" — 관세율표·분류사례·주해설·법령·결정례 다각도 확인
        </span>
      </div>

      {/* 검색어가 관세감면 조문 낱말이면 그 조문으로 가는 한 줄(문의 #76). */}
      <ExemptionHintLine q={q} />

      {/* 사무소 과거 신고가 다섯 조건을 전부 통과했을 때만 한 줄(2026-09-14 사용자 결정: 제안 카드는 숨긴다). */}
      <HistoryMatchLine proposal={proposalData && proposalData.query === q ? proposalData.data : null} onHighlight={proposalHighlight(onRowClick)} />

      {!exemptionCodeQuery && (
      <>
      <SubdivisionPreviewCard
        q={q}
        preview={subdivisionPreview}
        productDetails={tariffData?.productDetails}
        onOpenProve={onOpenProve}
        tariffItems={tariff ? tariff.items : []}
        tariffEngine={tariff ? { nameMatches: tariff.nameMatches || [], matchTokens: tariff.matchTokens || [], headingMatches: tariff.headingMatches || [], fallback: tariff.fallback || null } : null}
        proposal={proposalData && proposalData.query === q ? proposalData.data : null}
      />

      {/* 제안 카드(설계 v7 §7) — 요청만 한다(응답은 지도·표 마디 배지·후보 호 라우팅·위의 일치 줄이 쓴다).
          카드 자체는 2026-09-14 사용자 결정으로 숨김: 같은 신호가 지도·표에 이미 보이고, 이력이 없는 물품에서는
          진단 코드(lines_lt2·no_target…)만 남아 잡음이었다. */}
      <ProposalCard
        visible={false}
        query={q}
        productDetails={tariffData?.productDetails}
        ready={proposalReady}
        tariffItems={tariff ? tariff.items : []}
        onHighlight={proposalHighlight(onRowClick)}
        onLoaded={onProposalLoaded}
      />

      <NaverMeaningCandidates
        q={q}
        applied={tariffData?.meaningExpansion}
        initialProductDetails={tariffData?.productDetails}
        onApply={(candidate, confirmedProductDetails = {}, profileMeta = {}) => {
          const evidence = {
            type: candidate.type,
            title: candidate.title,
            description: candidate.type === "web" ? candidate.description : "",
            url: candidate.url || candidate.image_url || "",
            thumbnail_url: candidate.thumbnail_url || "",
            source: "naver_user_confirmed",
            originalQuery: q,
            product_details: confirmedProductDetails,
            profile_model: profileMeta.model || "",
            profile_evidence_summary: profileMeta.evidenceSummary || "",
            user_confirmed_at: new Date().toISOString(),
          };
          try {
            window.dispatchEvent(new CustomEvent("loginhs:product-details-applied", {
              detail: confirmedProductDetails,
            }));
          } catch { /* 오래된 브라우저에서는 검색 자체만 계속한다. */ }
          // 확장 검색어는 다른 sq가 되므로 이미 답한 결정트리 사실을 새 검색어로 이어 준다.
          const expandedQuery = buildNaverExpandedQuery(q, candidate, confirmedProductDetails);
          const confirmedFacts = copyTreeFacts(q, expandedQuery);
          setPendingSearch({
            q: expandedQuery,
            ts: Date.now(),
            mode: "auto",
            productDetails: confirmedProductDetails,
            meaningExpansion: evidence,
            confirmedFacts,
          });
        }}
      />
      </>
      )}

      <ProductIdentityGate
        q={q}
        enabled={!exemptionCodeQuery && productGroundingMode === "auto"}
        manual={!exemptionCodeQuery && productGroundingMode === "manual"}
        onRowClick={onRowClick}
        onOpenProve={onOpenProve}
      >
      <>
        <ExactAnswerCard q={q} cases={cases} onOpen={onPrecedentOpen} />
        <SearchRefinementPanel
          q={q}
          data={d}
          onRowClick={onRowClick}
          defaultOpen={!(subdivisionPreview && subdivisionPreview.q === q
            && (subdivisionPreview.loading || Boolean(subdivisionPreview.row?.decision_tree_shadow)))}
        />

      <SectionCard
        title="① 관세율표 검색결과"
        count={tariffCount}
        loading={!tariff}
      >
        <TariffCompareTable
          items={tariff ? tariff.items : []}
          headingMatches={tariff ? tariff.headingMatches : []}
          matchTokens={tariffMatchTokens}
          onRowClick={onRowClick}
        />
      </SectionCard>
      {tariff && tariffCount === 0 && (
        <div
          style={{
            fontSize: 12,
            color: "var(--audit-text-muted)",
            margin: "-6px 0 12px",
            padding: "0 4px",
          }}
        >
          관세율표 매칭 없음 — 좌측 검색창에서 단어를 줄이거나 "의미" 모드를 켜 보세요.
        </div>
      )}

      <SectionCard
        title="② 분류사례 검색결과"
        count={cases.length}
        loading={prec.loading}
        error={prec.error}
        onRetry={retryPrec}
      >
        <CaseTable
          items={cases}
          onOpen={onPrecedentOpen}
          matchTokens={precedentMatchTokens}
        />
      </SectionCard>

      <SectionCard
        title="③ 주·해설 검색결과"
        count={notes.items.length}
        loading={notes.loading}
        error={notes.error}
        onRetry={retryNotes}
      >
        <div>
          {notes.items.map((it, i) => {
            const cleanDesc = String(it.desc || "")
              .replace(/<a\s[^>]*>([^<]*)<\/a>/g, "$1")
              .replace(/<\/?[a-z][^>]*>/gi, "");
            const visibleDesc = buildHighlightSnippet(cleanDesc, noteMatchTokens) || cleanDesc;
            return (
              <div
                key={i}
                style={{
                  borderTop: i > 0 ? "1px solid var(--audit-border)" : "none",
                  padding: "8px 0",
                  fontSize: 12,
                }}
              >
                <div
                  style={{
                    fontFamily: "JetBrains Mono, monospace",
                    color: "var(--audit-text-muted)",
                    marginBottom: 4,
                  }}
                >
                  {highlightTokens(it.label, noteMatchTokens)}
                </div>
                <div
                  style={{
                    lineHeight: 1.6,
                    whiteSpace: "pre-line",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                  title={cleanDesc}
                >
                  {highlightTokens(visibleDesc, noteMatchTokens)}
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard
        title="④ 관련법령 검색결과"
        count={laws.items.length}
        loading={laws.loading}
        error={laws.error}
        onRetry={retryLaws}
      >
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={caseTh}>HSK</th>
              <th style={{ ...caseTh, width: "auto" }}>법령</th>
              <th style={{ ...caseTh, width: 90 }}>적용일</th>
            </tr>
          </thead>
          <tbody>
            {laws.items.map((it, i) => (
              <tr key={i} style={{ borderTop: "1px solid var(--audit-border)" }}>
                <td style={{ padding: 4, fontFamily: "JetBrains Mono, monospace", fontWeight: 600 }}>
                  {it.hsk}
                </td>
                <td style={{ padding: 4 }}>
                  {highlightTokens(it.label, lawMatchTokens)}
                </td>
                <td style={{ padding: 4, fontFamily: "JetBrains Mono, monospace", color: "var(--audit-text-muted)" }}>
                  {it.apply_dt}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>

      <SectionCard
        title="⑤ 결정례·사전심사 검색결과"
        count={rulings.length}
        loading={prec.loading}
        error={prec.error}
        onRetry={retryPrec}
      >
        <CaseTable
          items={rulings}
          onOpen={onPrecedentOpen}
          showSourceBadge
          matchTokens={precedentMatchTokens}
        />
      </SectionCard>
      </>
      </ProductIdentityGate>
    </div>
  );
}
